/**
 * Auto re-detect an auto-detectable pedal whose bridge binding has gone stale.
 *
 * ─── THE BUG THIS FIXES (root-caused 2026-09-02) ──────────────────────────
 *
 * Operator report: after OPButtons starts, the Helix buttons are greyed out and
 * "I have to re-detect the helix pedal to get them back."
 *
 * The chain, every link read from source:
 *
 *   1. `detectPortAcrossMesh` writes an EXPLICIT `bridgeId` (a row id) onto
 *      the pedal - that is per-item bridge affinity working as designed
 *      (DEC-169), so a control targets a known machine.
 *   2. `resolveBridge` does NOT fall back when that id stops resolving:
 *        if (!target) return primaryBridge();
 *        return bridgesSig().find((b) => b.id === target) ?? null;
 *      An absent id follows the default and self-heals. A DANGLING id
 *      returns null, permanently.
 *   3. `bridgeConnectedReactive` then reports false however healthy the mesh
 *      is, and `PresetChange.tsx` greys every control on exactly that.
 *   4. RE-DETECT rewrites the binding to a row that exists, and it works
 *      again - which is why that button is the only known cure.
 *
 * The sting: a GENERIC pedal stores an empty `bridgeId`, meaning "follow the
 * default", and recovers on its own. Only an AUTO-DETECTED pedal gets a
 * pinned id - so the Helix, the one device built so that "OPButtons should
 * just work", is the single case that cannot recover unaided. Its bridge
 * picker is hidden too (`ConnectedDevices.tsx`'s `<Show when={!model()
 * ?.autoDetectSubstring ...}>`), so the broken field is invisible.
 *
 * ─── WHY RE-DETECTION AND NOT A RESOLVER FALLBACK ─────────────────────────
 *
 * Making `resolveBridge` fall back to the primary when an id misses would fix
 * this in one line and would be WRONG: DEC-169 made affinity explicit
 * precisely so a control fires at a known machine, and a silent fallback
 * could send MIDI to the wrong laptop mid-set. Re-detection does not guess -
 * it asks every connected bridge which one actually has a port matching the
 * model's name, so the new binding is evidence, not a default.
 *
 * ─── THE TWO GUARDS, both load-bearing ────────────────────────────────────
 *
 * 1. It triggers on "the binding does not resolve AND some bridge is
 *    connected" - NOT on "the control looks greyed". Greyed has several
 *    causes (fault, no port, mode), and hanging a network call off a render
 *    state is how you build a loop.
 * 2. It runs ONCE per distinct connected-bridge set, never on a timer.
 *    `detectPortAcrossMesh` calls `GET /midi/ports` on every candidate, and
 *    this project has already been bitten by its own LAN sweep starving the
 *    Connect path (DEC-185). A failed attempt is not retried until the set of
 *    connected bridges actually changes, so a genuinely unplugged pedal costs
 *    one sweep per reconnect rather than a rising tide of them.
 *
 * Never throws. Never clears an existing binding: if detection finds nothing
 * the pedal is left exactly as it was and the control stays honestly dark
 * (DEC-124/138 - a rig that can send nothing says so).
 */

import { createEffect, on } from 'solid-js';
import {
  activePedals, updatePedal, pedalModel, type GuitarPedal,
} from '../layout/projectStore.ts';
import {
  bridges, getConnection, connectionsVersion, bridgeConnectedReactive,
} from '../opbridge/store.ts';
import type { OpbridgeHttpClient } from '../opbridge/httpClient.ts';
import { detectPortAcrossMesh } from './detect.ts';
import { pedalBridgeRowId, pedalNodeMigration } from './bridgeBinding.ts';

/** Connected bridges, default first - same ordering `ConnectedDevices` uses,
 *  so the tie-break when two machines expose the same port name is identical
 *  whether the operator pressed RE-DETECT or this did it for them. */
function meshCandidates(): { bridgeId: string; client: OpbridgeHttpClient }[] {
  const all = bridges();
  const ordered = [...all.filter((b) => b.is_primary), ...all.filter((b) => !b.is_primary)];
  const out: { bridgeId: string; client: OpbridgeHttpClient }[] = [];
  for (const b of ordered) {
    const conn = getConnection(b.id);
    if (conn?.status() === 'connected') out.push({ bridgeId: b.id, client: conn.httpClient() });
  }
  return out;
}

/** A pedal needs re-binding when its model can be auto-detected AND either
 *  its bridge id no longer resolves to a real row, or it has no port. Both
 *  are states RE-DETECT fixes and nothing else does. */
function needsRebind(pd: GuitarPedal): boolean {
  const model = pedalModel(pd.modelId);
  if (!model?.autoDetectSubstring) return false;   // generic pedals self-heal
  if (!pd.port) return true;
  // WIDENED 2026-09-02.
  //
  // ⚠️ HONESTY NOTE, because the first version of this comment overclaimed and
  // is worth not repeating: this widening was prompted by an operator report
  // that the fix "isn't auto restoring", with the tile reading "no bridge" -
  // and that report turned out to come from a DIFFERENT PC which may never
  // have had the fix loaded at all. So it is NOT established that the rig hit
  // this case, and v1 was never given a fair test. The widening is kept
  // because it is correct on its own terms, not because it was proven to be
  // the cure.
  //
  // The reasoning that does stand without that report: v1 fired only when the
  // binding failed to RESOLVE (a dangling row id). But a bound row that still
  // EXISTS and is merely DISCONNECTED cannot self-heal either - `resolveBridge`
  // finds it happily, so v1 skipped it - while the same pedal may be reachable
  // through a different bridge that is up. Both states render the identical
  // "no bridge" text, so the tile could never have told them apart.
  //
  // `bridgeConnectedReactive` covers all three cases in one read: it resolves
  // (absent id -> default, dangling id -> null) and then checks the connection.
  // Not connected, for any of those reasons, means re-detection might help.
  //
  // Safe because detection only ever rebinds to a bridge that ACTUALLY reports
  // a matching port. If the pedal's own bridge is merely down and no other
  // machine has a Helix port, nothing is found and the binding is left exactly
  // as it was - the control stays honestly dark rather than being re-pointed
  // at a machine that cannot reach the pedal.
  return !bridgeConnectedReactive(pedalBridgeRowId(pd));
}

/** The `nodeId` of a local bridge row, or null if the row is unknown here or
 *  has not been backfilled by `/health` yet. Named rather than inlined so the
 *  "no node yet is NORMAL, not an error" case has somewhere to be documented. */
function findBridgeByNode_localRowNode(rowId: string): string | null {
  return bridges().find((b) => b.id === rowId)?.nodeId ?? null;
}

/** Guard for rule 2: the connected-bridge set this pedal was last attempted
 *  against. Re-attempted only when that set changes. */
const attemptedFor = new Map<string, string>();
let inFlight = false;
/** Set when a trigger arrives DURING a sweep, so it is coalesced into one more
 *  pass rather than dropped. Without this, an early `return` on `inFlight`
 *  silently loses that trigger and nothing re-runs until the connected-bridge
 *  set next changes - measured 2026-09-02: three pedals seeded in one tick,
 *  the effect fired after the first, and the other two were skipped entirely
 *  because their change landed mid-sweep. Not a test artifact: a project whose
 *  pedals load while a sweep is in flight would hit exactly this on the rig. */
let rerunRequested = false;

export async function rebindStalePedals(): Promise<void> {
  if (inFlight) { rerunRequested = true; return; }
  inFlight = true;
  try {
    do {
      rerunRequested = false;
      const candidates = meshCandidates();
      if (candidates.length === 0) return;         // nothing to ask
      const setKey = candidates.map((c) => c.bridgeId).sort().join(',');

      const stale = activePedals().filter(
        (pd) => needsRebind(pd) && attemptedFor.get(pd.id) !== setKey,
      );

      for (const pd of stale) {
        attemptedFor.set(pd.id, setKey);           // mark BEFORE awaiting, so a
        // failure cannot be retried by a re-entrant run against the same set.
        const substring = pedalModel(pd.modelId)?.autoDetectSubstring;
        if (!substring) continue;
        try {
          const match = await detectPortAcrossMesh(candidates, substring);
          if (!match) continue;                    // leave the binding alone
          // Write BOTH. `bridgeNodeId` is the authority everywhere else, so
          // omitting it here would let the rebind sweep quietly reinstate the
          // device-local id as this pedal's binding - reopening the exact
          // last-writer-wins fight between machines that node ids close.
          // `bridgeId` stays alongside it as the pre-migration fallback.
          const node = findBridgeByNode_localRowNode(match.bridgeId);
          updatePedal(pd.id, {
            port: match.port,
            bridgeId: match.bridgeId,
            ...(node ? { bridgeNodeId: node } : {}),
          });
          console.info(
            `[pedals] re-bound "${pd.name}" to ${match.port} on bridge ${match.bridgeId}` +
            `${node ? ` (node ${node})` : ' (no node id yet - legacy binding)'} ` +
            '(previous binding did not resolve to a connected bridge)',
          );
        } catch { /* unreachable bridge mid-sweep - next connect change retries */ }
      }
    } while (rerunRequested);
  } finally {
    inFlight = false;
  }
}

/**
 * Upgrade legacy pedals from a device-local `bridgeId` to a stable
 * `bridgeNodeId`, in place, once the local rows can supply the node.
 *
 * Runs as its own reactive sweep rather than inside `rebindStalePedals`
 * because the two answer different questions. Rebind only looks at pedals
 * that are NOT connected; a pedal happily bound to a correct local row still
 * carries the id that breaks on every other machine, and it is exactly the
 * one nobody would notice until a second machine pulled the document.
 *
 * **Idempotent and additive.** It writes only `bridgeNodeId`, only when
 * absent, and only when a local row actually supplies one - so a rig with no
 * bridges, or one whose `/health` backfill has not landed yet, simply does
 * nothing and tries again when the bridge list next changes. `bridgeId` is
 * never cleared: see `bridgeBinding.ts` on why keeping it is the
 * non-destructive choice on a document other machines read concurrently.
 *
 * **Why it is safe to let this write to a synced document.** Every machine
 * computes the SAME `bridgeNodeId` for the same physical bridge, so
 * concurrent migrations converge instead of fighting - which is the whole
 * property the local row id lacked.
 */
export function migratePedalBridgeBindings(): void {
  const rows = bridges();
  if (rows.length === 0) return;
  const nodeOf = (rowId: string): string | null =>
    rows.find((b) => b.id === rowId)?.nodeId ?? null;

  for (const pd of activePedals()) {
    const patch = pedalNodeMigration(pd, nodeOf);
    if (!patch) continue;
    updatePedal(pd.id, patch);
    console.info(
      `[pedals] migrated "${pd.name}" binding to node ${patch.bridgeNodeId} ` +
      `(was local row id ${pd.bridgeId}) - a row id does not survive the mesh`,
    );
  }
}

/** Wire the migration sweep. Call once at boot, BEFORE `installPedalRebind`
 *  so a pedal that can be migrated is migrated before the rebind sweep gets
 *  a chance to treat it as stale and re-detect it. */
export function installPedalNodeMigration(): void {
  createEffect(on(
    () => bridges().map((b) => `${b.id}:${b.nodeId ?? ''}`).join(',')
      + '|' + activePedals().map((p) => `${p.id}:${p.bridgeNodeId ?? ''}`).join(','),
    () => { migratePedalBridgeBindings(); },
  ));
}

/** Wire the reactive trigger. Call once at boot, alongside the other
 *  `install*` wiring in App.tsx.
 *
 *  `connectionsVersion()` is read explicitly and is LOAD-BEARING: `getConnection`
 *  is a plain `Map.get` and tracks nothing, so an effect depending on
 *  `bridges()` alone never re-runs when a connection object appears or is
 *  swapped. That exact omission has silently disabled whole modules in this
 *  codebase before - see the note in `opbridge/performanceMode.ts` (it once kept the since-removed
 *  Waves doc sync asleep for a whole session). */
export function installPedalRebind(): void {
  createEffect(on(
    () => {
      void connectionsVersion();
      // Track the PEDALS as well as the bridges. Tracking only the connected
      // set looked right and was wrong: at boot the bridge connects BEFORE the
      // project (and therefore its pedals) has loaded, so the sweep ran against
      // an empty list and never re-ran - a stale binding would sit there
      // forever. That is not a hypothetical ordering: a doc-store project pull
      // lands well after the first connect. Caught by
      // `pedal_rebinds_when_bridge_id_dangles.ts` failing against the first
      // version of this file.
      const conn = meshCandidates().map((c) => c.bridgeId).join(',');
      const peds = activePedals()
        // `bridgeNodeId` is in the key deliberately: it is backfilled
        // asynchronously by the first /health, so a pedal can go from
        // unresolvable to resolvable with nothing else changing. Without it
        // here, that pedal would sit stale until some unrelated bridge event
        // happened to re-trigger the sweep.
        .map((p) => `${p.id}:${p.bridgeNodeId ?? ''}:${p.bridgeId ?? ''}:${p.port ?? ''}`)
        .join(',');
      return `${conn}|${peds}`;
    },
    () => { void rebindStalePedals(); },
  ));
}
